/**
 * 情境感知行动按钮 (Context-Aware CTA)
 * 根据风险等级动态调整视觉和文案
 * - High: 红色急救风格，呼吸动效
 * - Medium: 品牌色专业风格，微光效果
 * - Low: 绿色养护风格，稳重
 * - Unknown: 紫色通用指引风格
 */

import React from "react";
import { useTranslation } from "react-i18next";
import {
  Zap,
  ClipboardList,
  Shield,
  Search,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

type SeverityLevel = "high" | "medium" | "low" | "unknown";

interface SmartCTABarProps {
  severity: SeverityLevel;
  onAction: () => void;
  isLoading?: boolean;
  className?: string;
}

// 风险等级配置映射
const SEVERITY_CONFIG = {
  high: {
    // 红色急救风格
    buttonClass:
      "bg-red-600 hover:bg-red-500 text-white shadow-[0_0_20px_rgba(220,38,38,0.4)]",
    animation: "animate-pulse",
    subtextClass: "text-red-300",
    Icon: Zap,
  },
  medium: {
    // 品牌色专业风格
    buttonClass:
      "bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/30",
    animation: "",
    subtextClass: "text-muted-foreground",
    Icon: ClipboardList,
  },
  low: {
    // 绿色养护风格
    buttonClass: "bg-emerald-500 hover:bg-emerald-400 text-white shadow-md",
    animation: "",
    subtextClass: "text-emerald-300 dark:text-emerald-400",
    Icon: Shield,
  },
  unknown: {
    // 紫色通用指引风格
    buttonClass: "bg-violet-600 hover:bg-violet-500 text-white shadow-lg",
    animation: "",
    subtextClass: "text-violet-300 dark:text-violet-400",
    Icon: Search,
  },
};

export const SmartCTABar: React.FC<SmartCTABarProps> = ({
  severity,
  onAction,
  isLoading = false,
  className,
}) => {
  const { t } = useTranslation("assessments");
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.unknown;
  const Icon = config.Icon;

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 px-3 sm:px-4 pt-3 sm:pt-4 pb-[max(0.5rem,env(safe-area-inset-bottom))] bg-gradient-to-t from-background via-background/95 to-transparent z-50",
        className
      )}
    >
      <div className="w-full flex flex-col items-center gap-1">
        <div className="max-w-md w-full mx-auto flex flex-col items-center gap-2">
          {/* 价值钩子 (Micro-copy) */}
          <span
            className={cn(
              "text-xs font-medium tracking-wide text-center",
              config.subtextClass
            )}
          >
            {t(`smartCta.${severity}.subtext`)}
          </span>

          {/* 主按钮 */}
          <Button
            onClick={onAction}
            disabled={isLoading}
            className={cn(
              "w-full py-6 px-6 rounded-full",
              "flex items-center justify-center gap-2.5",
              "transition-all duration-300 transform active:scale-95",
              "font-bold text-base",
              config.buttonClass,
              config.animation
            )}
            size="lg"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Icon className="w-5 h-5" />
            )}
            <span>{t(`smartCta.${severity}.text`)}</span>
            <ArrowRight className="w-5 h-5 opacity-80" />
          </Button>
        </div>

        {/* 免责声明 - 允许全宽显示以保持单行 */}
        <p className="text-[10px] text-muted-foreground/60 text-center leading-relaxed mt-1 whitespace-nowrap overflow-hidden text-overflow-ellipsis w-full px-4">
          {t("pipeline.disclaimer")}
        </p>
      </div>
    </div>
  );
};

export default SmartCTABar;
